// Free, key-less weather via Open-Meteo (CORS-enabled).

export interface Weather {
  temp: number;
  code: number;
  label: string;
  emoji: string;
}

export interface GeoResult {
  latitude: number;
  longitude: number;
  label: string;
}

/** Map a WMO weather code to a short label + emoji. */
export function describeWeather(code: number): { label: string; emoji: string } {
  if (code === 0) return { label: "Clear", emoji: "☀️" };
  if (code <= 2) return { label: "Partly cloudy", emoji: "⛅" };
  if (code === 3) return { label: "Cloudy", emoji: "☁️" };
  if (code === 45 || code === 48) return { label: "Fog", emoji: "🌫️" };
  if (code >= 51 && code <= 57) return { label: "Drizzle", emoji: "🌦️" };
  if (code >= 61 && code <= 67) return { label: "Rain", emoji: "🌧️" };
  if (code >= 71 && code <= 77) return { label: "Snow", emoji: "❄️" };
  if (code >= 80 && code <= 82) return { label: "Showers", emoji: "🌦️" };
  if (code === 85 || code === 86) return { label: "Snow showers", emoji: "🌨️" };
  if (code >= 95) return { label: "Thunderstorm", emoji: "⛈️" };
  return { label: "—", emoji: "🌡️" };
}

export async function geocode(name: string): Promise<GeoResult | null> {
  const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(name)}&count=1`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = (await res.json()) as {
    results?: { latitude: number; longitude: number; name: string; admin1?: string }[];
  };
  const r = data.results?.[0];
  if (!r) return null;
  return {
    latitude: r.latitude,
    longitude: r.longitude,
    label: r.admin1 ? `${r.name}, ${r.admin1}` : r.name,
  };
}

export async function fetchWeather(
  latitude: number,
  longitude: number,
  unit: "F" | "C",
): Promise<Weather | null> {
  const tempUnit = unit === "F" ? "fahrenheit" : "celsius";
  const url =
    `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}` +
    `&current=temperature_2m,weather_code&temperature_unit=${tempUnit}`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = (await res.json()) as {
    current?: { temperature_2m: number; weather_code: number };
  };
  if (!data.current) return null;
  const { label, emoji } = describeWeather(data.current.weather_code);
  return { temp: Math.round(data.current.temperature_2m), code: data.current.weather_code, label, emoji };
}
